"use no memo";

import { useState } from "react";
import { EmailSettingsSchema } from "@repo/validators";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { SettingsHeader } from "~/components/layout/settings-header";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Dialog, DialogContent } from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { ModalInput } from "~/components/ui/modal-input";
import { useConfiguration } from "~/hooks/use-configuration";
import { api } from "~/lib/api";

export const Route = createFileRoute("/_main/settings/_layout/email")({
  component: EmailSettings,
});

function EmailSettings() {
  const { t, i18n } = useTranslation("common");

  const [testDialogOpen, setTestDialogOpen] = useState(false);

  const { emailSettings } = useConfiguration();

  const [testEmailTo, setTestEmailTo] = useState("");

  const { mutate: sendTestEmail } = api.config.sendTestEmail.useMutation({
    onSuccess() {
      toast.success(t("Email.test_mail_sent"));
    },
  });

  const { mutate: updateEmailSettings } =
    api.config.updateEmailSettings.useMutation({
      onSuccess() {
        toast.success(
          t("update_item_success", { item: t("Config.email_settings") }),
        );
      },
    });

  const form = useForm({
    schema: EmailSettingsSchema(i18n),
    defaultValues: {
      smtpHost: emailSettings?.smtpHost ?? "",
      smtpUsername: emailSettings?.smtpUser ?? "",
      smtpPassword: emailSettings?.smtpPassword ?? "",
      smtpPort: emailSettings?.smtpPort ?? undefined,
      smtpFrom: emailSettings?.smtpFrom ?? "",
    },
  });

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  const handleTest = form.handleSubmit((formData) => {
    if (!testEmailTo) {
      setTestDialogOpen(true);
      return;
    }

    sendTestEmail({
      ...formData,
      to: testEmailTo,
    });
  });

  const handleSubmit = form.handleSubmit((formData) => {
    updateEmailSettings(formData);
  });

  return (
    <Form {...form}>
      <SettingsHeader
        title={t("email")}
        buttonLabel={t("save")}
        buttonDisabled={!isDirty}
        buttonAction={() => {
          void handleSubmit();
        }}
      />

      <Dialog
        open={testDialogOpen}
        onOpenChange={(open) => {
          if (open === false) {
            setTestEmailTo("");
          }
          setTestDialogOpen(open);
        }}
      >
        <DialogContent className="p-4 gap-2 flex flex-col">
          <Label>{t("Email.test_mail_to")}</Label>
          <Input
            type="email"
            value={testEmailTo}
            onChange={(e) => setTestEmailTo(e.target.value)}
          />
          <Button variant="success" onClick={handleTest}>
            Send
          </Button>
        </DialogContent>
      </Dialog>
      <form className="flex flex-col gap-4">
        <Card>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              <FormField
                control={form.control}
                name="smtpFrom"
                render={({ field }) => (
                  <FormItem className="col-span-full">
                    <FormLabel>{t("Email.smtp_from")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormDescription>
                      {t("Email.smtp_from_description")}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>{t("Email.server_settings_title")}</CardTitle>
            <CardDescription>
              {t("Email.server_settings_description")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              <FormField
                control={form.control}
                name="smtpUsername"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Email.smtp_user")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="smtpPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Email.smtp_pass")}</FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="smtpHost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Email.smtp_host")}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="smtpPort"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Email.smtp_port")}</FormLabel>
                    <FormControl>
                      <ModalInput type="number" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                variant="success"
                className="col-span-full"
                onClick={handleTest}
              >
                Send test mail
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </Form>
  );
}
